/**
 * App.tsx — nsentry SPA router
 *
 * Purpose:    React Router route definitions for sentry.nself.org.
 *             Includes 7 ɳSentry plugin dashboards under /dashboard/*, the
 *             authed SaaS app zone under /app/*, and public status pages
 *             under /s/:slug.
 * Inputs:     URL path
 * Outputs:    Routed page component wrapped in SiteLayout, DashboardLayout, or AppLayout
 * Constraints: All routes client-side; no SSR
 * SOT:        T-P3-E5-W2-S4-T01 (dashboards); T-P3-E3-W2-S5-T01 (site); nsentry SaaS app zone
 */
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './lib/auth-context'
import { RequireAuth } from './components/RequireAuth'
import { SiteLayout } from './layouts/SiteLayout'
import { DashboardLayout } from './layouts/DashboardLayout'
import { AppLayout } from './layouts/AppLayout'
import { HomePage } from './pages/HomePage'
import { ChangelogPage } from './pages/ChangelogPage'
import { DemoPage } from './pages/DemoPage'
import { FeaturesPage } from './pages/FeaturesPage'
import { HowItWorksPage } from './pages/HowItWorksPage'
import { InvitePage } from './pages/InvitePage'
import { InviteCodePage } from './pages/InviteCodePage'
import { MigratePage } from './pages/MigratePage'
import { OnboardPage } from './pages/OnboardPage'
import { PricingPage } from './pages/PricingPage'
import { ComparePage } from './pages/ComparePage'
import { SelfHostPage } from './pages/SelfHostPage'
// Legal set (standard SaaS templates — marked "review with counsel")
import { TermsPage } from './pages/legal/TermsPage'
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage'
import { GdprPage } from './pages/legal/GdprPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { StatusPublicPage } from './pages/StatusPublicPage'
// Dashboard pages (7 ɳSentry plugin dashboards)
import { UptimeDashboard } from './pages/dashboard/UptimeDashboard'
import { StatusPageDashboard } from './pages/dashboard/StatusPageDashboard'
import { IncidentsDashboard } from './pages/dashboard/IncidentsDashboard'
import { AlertsDashboard } from './pages/dashboard/AlertsDashboard'
import { SloDashboard } from './pages/dashboard/SloDashboard'
import { SyntheticDashboard } from './pages/dashboard/SyntheticDashboard'
import { RumDashboard } from './pages/dashboard/RumDashboard'
import { SentryTestPage } from './pages/SentryTestPage'
// SaaS app zone (authed)
import { LoginPage } from './pages/app/LoginPage'
import { SignupPage } from './pages/app/SignupPage'
import { MonitorsPage } from './pages/app/MonitorsPage'
import { MonitorDetailPage } from './pages/app/MonitorDetailPage'
import { IncidentsPage } from './pages/app/IncidentsPage'
import { ErrorsPage } from './pages/app/ErrorsPage'
import { RumPage } from './pages/app/RumPage'
import { CronPage } from './pages/app/CronPage'
import { StatusPagesPage } from './pages/app/StatusPagesPage'
import { OverviewPage } from './pages/app/OverviewPage'
import { ApiKeysPage } from './pages/app/ApiKeysPage'
import { BillingPage } from './pages/app/BillingPage'
import { TeamPage } from './pages/app/TeamPage'
import { SettingsPage } from './pages/app/SettingsPage'
import { WelcomePage } from './pages/app/WelcomePage'

/**
 * Stripe Checkout returns to /billing?status=… (ping_api success/cancel URLs);
 * forward into the app zone keeping the query string intact.
 */
function BillingReturnRedirect() {
  const location = useLocation()
  return <Navigate to={`/app/billing${location.search}`} replace />
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Marketing / public site */}
          <Route element={<SiteLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/invite/:code" element={<InviteCodePage />} />
            <Route path="/migrate" element={<MigratePage />} />
            <Route path="/onboard/:step" element={<OnboardPage />} />
            <Route path="/onboard" element={<Navigate to="/onboard/1" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            {/* /privacy historically served the comparison page; it now lives
                at /compare and /privacy points at the real privacy policy. */}
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/gdpr" element={<GdprPage />} />
            <Route path="/self-host" element={<SelfHostPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Observability smoke-test — dev-only; never index */}
          <Route path="/sentry-test" element={<SentryTestPage />} />

          {/* Dashboard — 7 ɳSentry plugin dashboards */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Navigate to="/dashboard/uptime" replace />} />
            <Route path="uptime" element={<UptimeDashboard />} />
            <Route path="status" element={<StatusPageDashboard />} />
            <Route path="incidents" element={<IncidentsDashboard />} />
            <Route path="alerts" element={<AlertsDashboard />} />
            <Route path="slo" element={<SloDashboard />} />
            <Route path="synthetic" element={<SyntheticDashboard />} />
            <Route path="rum" element={<RumDashboard />} />
          </Route>

          {/* Stripe Checkout return URL → app-zone billing (query preserved) */}
          <Route path="/billing" element={<BillingReturnRedirect />} />

          {/* SaaS app zone — public auth pages */}
          <Route path="/app/login" element={<LoginPage />} />
          <Route path="/app/signup" element={<SignupPage />} />

          {/* First-signup onboarding — authed but outside AppLayout chrome */}
          <Route
            path="/app/welcome"
            element={
              <RequireAuth>
                <WelcomePage />
              </RequireAuth>
            }
          />

          {/* SaaS app zone — authed */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/app/monitors" replace />} />
            <Route path="monitors" element={<MonitorsPage />} />
            <Route path="monitors/:id" element={<MonitorDetailPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="errors" element={<ErrorsPage />} />
            <Route path="rum" element={<RumPage />} />
            <Route path="cron" element={<CronPage />} />
            <Route path="status-pages" element={<StatusPagesPage />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Public per-tenant status page */}
          <Route path="/s/:slug" element={<StatusPublicPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
